/** Prefixo global da API. O front nunca escreve caminho na mão. */
export const PREFIXO_API = '/api';
export const CAMINHO_WS = '/ws';

export const ROTAS = {
  auth: {
    login: '/auth/login',
    totp: '/auth/totp',
    totpAtivar: '/auth/totp/ativar',
    totpConfirmar: '/auth/totp/confirmar',
    recuperacao: '/auth/recuperacao',
    senhaEsquecer: '/auth/senha/esquecer',
    senhaRedefinir: '/auth/senha/redefinir',
    refresh: '/auth/refresh',
    logout: '/auth/logout',
    eu: '/auth/eu',
  },
  usuarios: '/usuarios',
  maquinas: '/maquinas',
  sessoes: '/sessoes',
  /**
   * Os PROJETOS da frota — (máquina, cwd normalizado) — que a tela de contas
   * marca desde o ADR-0020. Rota própria, e não um agregado derivado no front
   * a partir de `GET /sessoes`: a chave do projeto é decidida pelo SERVIDOR
   * (a normalização depende do papel da máquina), e um cliente que a montasse
   * sozinho inventaria grant que não casa com nada.
   */
  projetos: '/projetos',
  permissoes: '/permissoes',
  arquivos: '/arquivos',
  gravacoes: '/gravacoes',
  trabalhos: '/trabalhos',
  notificacoes: {
    raiz: '/notificacoes',
    chavePublica: '/notificacoes/chave-publica',
    inscricoes: '/notificacoes/inscricoes',
    preferencias: '/notificacoes/preferencias',
    teste: '/notificacoes/teste',
  },
  uso: {
    /** As duas de baixo ficam até o painel parar de consumi-las (ADR-0021). */
    resumo: '/uso/resumo',
    cota: '/uso/cota',
    painel: '/uso/painel',
    sessoes: '/uso/sessoes',
  },
  agente: '/agente',
  saude: '/saude',
  /**
   * A tela de Configurações do OpenCode (F16, D17): só ADMIN. O campo da chave
   * se chama `segredo` no corpo, e é o que a auditoria redige pelo nome.
   */
  configuracoes: {
    opencode: '/configuracoes/opencode',
    chaveOpencode: '/configuracoes/opencode/chave',
    testarChaveOpencode: '/configuracoes/opencode/chave/testar',
  },
  /** O catálogo do OpenRouter para as telas de criar (D12): OPERADOR+. */
  opencode: {
    modelos: '/opencode/modelos',
  },
  admin: {
    saude: '/admin/saude',
    auditoria: '/admin/auditoria',
    atividades: '/admin/atividades',
  },
  /**
   * O toolkit e o dicionário de memória (F3 da revisão de out/2026, ADR-0026).
   * Ler: todo papel, recortado por `veARotina`. Criar, decidir, marcar e
   * arquivar: OPERADOR+ (D20). Os caminhos por id estão em `CAMINHOS`: é o
   * cuid no caminho que a auditoria reconhece como alvo — um NOME ali viraria
   * parte do nome da ação.
   */
  toolkit: {
    rotinas: '/toolkit/rotinas',
    propostas: '/toolkit/propostas',
    alvos: '/toolkit/alvos',
    verbetes: '/toolkit/verbetes',
  },
} as const;

/**
 * Os sub-caminhos por id, declarados UMA vez em vez de interpolados na mão em
 * cada tela. Eram strings soltas no front; a de `redefinicao` (ADR-0020) foi a
 * que justificou juntá-las — errar uma letra num template literal só aparece
 * como 404 em tempo de execução, e só para quem clicar naquele botão.
 */
export const CAMINHOS = {
  visibilidadeDoUsuario: (id: string): string => `${ROTAS.usuarios}/${id}/visibilidade`,
  /**
   * ADMIN dispara a redefinição de senha da conta (ADR-0020).
   *
   * Rota PRÓPRIA, e não a pública `auth.senhaEsquecer`: aquela é
   * anti-enumeração de propósito — responde igual exista ou não a conta, e
   * dispara o e-mail sem `await` para o tempo não denunciar nada. Para o admin
   * essa indistinção não serve: ele já sabe que a conta existe, e o que ele
   * precisa saber é se o envio SAIU. Esta espera o SMTP e devolve falha de
   * verdade, e de carona entra na trilha de auditoria pelo interceptor global
   * — que a rota pública, sendo `@Publica`, grava com usuário nulo.
   */
  redefinicaoDoUsuario: (id: string): string => `${ROTAS.usuarios}/${id}/redefinicao`,
  visibilidadeDaSessao: (id: string): string => `${ROTAS.sessoes}/${id}/visibilidade`,
  /** Toolkit (F3): os sub-caminhos por id da rotina, do alvo, da proposta e do verbete. */
  rotina: (id: string): string => `${ROTAS.toolkit.rotinas}/${id}`,
  versoesDaRotina: (id: string): string => `${ROTAS.toolkit.rotinas}/${id}/versoes`,
  extensoesDaRotina: (id: string): string => `${ROTAS.toolkit.rotinas}/${id}/extensoes`,
  marcacoesDaRotina: (id: string): string => `${ROTAS.toolkit.rotinas}/${id}/marcacoes`,
  decisaoDaProposta: (id: string): string => `${ROTAS.toolkit.propostas}/${id}/decisao`,
  alvo: (id: string): string => `${ROTAS.toolkit.alvos}/${id}`,
  verbete: (id: string): string => `${ROTAS.toolkit.verbetes}/${id}`,
  versoesDoVerbete: (id: string): string => `${ROTAS.toolkit.verbetes}/${id}/versoes`,
} as const;

/**
 * Cookie do refresh: restrito ao módulo de autenticação.
 *
 * Era `/api/auth/refresh`, o mais estreito possível — e estreito demais: o
 * navegador então **não mandava o cookie para `/api/auth/logout`**, que lia
 * `undefined` e não revogava nada. A família seguia válida por sete dias
 * depois de a pessoa sair, e o e2e não via porque injetava o cookie à mão.
 *
 * `/api/auth` cobre as duas rotas e continua fora de todo o resto da API: o
 * refresh não acompanha chamada de sessão, de arquivo nem de gravação.
 */
export const COOKIE_REFRESH = 'remoto_refresh';
export const CAMINHO_COOKIE_REFRESH = `${PREFIXO_API}/auth`;

/**
 * O caminho que o cookie tinha antes, e que ainda vive em navegador aberto.
 *
 * Trocar o `path` **não substitui** o cookie antigo: `path` faz parte da
 * identidade dele. O navegador passa a guardar DOIS `remoto_refresh` e, ao
 * chamar `/api/auth/refresh`, manda os dois — com o de caminho mais longo (o
 * antigo, já gasto) na frente, que é justamente o que o servidor lê. Resultado
 * medido no uso real: deslogava a cada F5, para sempre, porque nada nunca
 * sobrescrevia o cookie velho.
 *
 * Some quando puder: depois que todo mundo tiver entrado uma vez com o
 * `clearCookie` deste caminho, isto aqui não serve mais para nada.
 */
export const CAMINHO_COOKIE_REFRESH_LEGADO = `${PREFIXO_API}${ROTAS.auth.refresh}`;
